import { useMemo, useState } from "react";

import { AuthService, type ValidateSignupKeyResponse } from "@/api";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/use-auth";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";

interface Step3Props {
  email: string;
  fullName: string;
  signupKey: string;
  workspaceInfo: ValidateSignupKeyResponse | null;
  onBack: () => void;
  onComplete: () => void;
}

const MIN_PASSWORD_LENGTH = 12;

/**
 * Step 3 — set password + submit registration. On success the
 * provider auto-logs the new user in so the wizard can redirect
 * straight to the dashboard.
 */
export function RegisterStep3({
  email,
  fullName,
  signupKey,
  workspaceInfo,
  onBack,
  onComplete,
}: Step3Props) {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const passwordHint = useMemo(() => {
    if (password.length === 0) {
      return t("auth.register.step3.passwordHintDefault", {
        min: MIN_PASSWORD_LENGTH,
      });
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      return t("auth.register.step3.passwordHintShort", {
        remaining: MIN_PASSWORD_LENGTH - password.length,
      });
    }
    return undefined;
  }, [password, t]);

  const { mutate, isLoading, error } = useMutation(
    () =>
      AuthService.register({
        email,
        full_name: fullName,
        password,
        signup_key: signupKey,
      }),
    {
      onSuccess: async () => {
        // Auto-login so the user lands on the dashboard signed in.
        try {
          await login(email, password);
        } catch {
          // Auto-login failed — fall back to redirecting to the
          // login page; the user can sign in manually. The
          // register succeeded so the credential is valid.
        }
        onComplete();
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(
        t("auth.register.step3.errors.tooShort", { min: MIN_PASSWORD_LENGTH })
      );
      return;
    }
    if (password !== confirm) {
      setValidationError(t("auth.register.step3.errors.mismatch"));
      return;
    }
    void mutate(undefined);
  };

  return (
    <div>
      <h2 className="text-xl font-medium text-ocean-800">
        {t("auth.register.step3.title")}
      </h2>
      <p className="mt-1 text-sm text-neutral-600">
        {t("auth.register.step3.subtitle")}
      </p>

      {workspaceInfo?.tenant_name && (
        <div className="mt-4 rounded-md border border-ocean-100 bg-ocean-50 px-3 py-2">
          <div className="text-xs font-medium uppercase tracking-wide text-ocean-600">
            {t("auth.register.step3.tenantLabel")}
          </div>
          <div className="mt-0.5 text-sm font-medium text-ocean-800">
            {workspaceInfo.tenant_name}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <FormField
          label={t("auth.register.step3.passwordLabel")}
          type="password"
          value={password}
          onChange={setPassword}
          placeholder={t("auth.register.step3.passwordPlaceholder", {
            min: MIN_PASSWORD_LENGTH,
          })}
          hint={passwordHint}
          autoComplete="new-password"
          required
        />
        <FormField
          label={t("auth.register.step3.confirmLabel")}
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
        />

        {validationError && (
          <Alert variant="danger">{validationError}</Alert>
        )}
        {error && !validationError && (
          <Alert variant="danger">
            {error instanceof ApiError && error.status === 422
              ? t("auth.register.step3.errors.registerFailed")
              : error.message}
          </Alert>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
          >
            {t("auth.register.step3.backButton")}
          </Button>
          <Button
            type="submit"
            disabled={
              isLoading ||
              password.length < MIN_PASSWORD_LENGTH ||
              password !== confirm
            }
            className="flex-1"
          >
            {isLoading
              ? t("auth.register.step3.submitLoading")
              : t("auth.register.step3.submitButton")}
          </Button>
        </div>
      </form>
    </div>
  );
}
